/** 保存可能なフィルタ条件 */
export type UserSearchFilter = {
  keyword?: string
  occupation?: string
  skill?: string
  careerTag?: string
  intention?: string
  lastAccessType?: string
  age?: string
  portfolioStatus?: string
  desiredOccupation?: string
  desiredAttendanceFrequency?: string
  desiredLocation?: string
  desiredCondition?: string
  desiredIncomeMax?: string
  desiredIncomeMin?: string
  currentIncomeMax?: string
  currentIncomeMin?: string
  prefecture?: string
  onlyHasReferralComment?: string
}

/** ソート条件 */
export type UserSortCondition = {
  sort?: string
}

/** 表示条件（localStorageで管理） */
export type UserDisplayCondition = {
  onlyHasDraft?: string
  hasNoDraft?: string
  onlyHasComment?: string
  hasNoComment?: string
  excludeOwnRead?: string
  excludeMemberRead?: string
  excludeTalentPoolRegistered?: string
  showAll?: string
}

/** URLクエリで管理する条件 = フィルタ条件 + ソート条件 */
export type UserSearchQuery = UserSearchFilter & UserSortCondition

/** 全条件の結合型（後方互換） */
export type UserSearchCondition = UserSearchQuery & UserDisplayCondition

export type IntentionOption = {
  value: number
  label: string
  color: TagColor
}

export const intentionOfJobChangeType = {
  IN_3_MONTHS: 1,
  IN_6_MONTHS: 2,
  IN_1_YEAR: 3,
  NOT_NOW: 4,
}

export const intentionOfJobChangeOptions: IntentionOption[] = [
  {
    value: intentionOfJobChangeType.IN_3_MONTHS,
    label: '3ヶ月以内',
    color: 'primary',
  },
  {
    value: intentionOfJobChangeType.IN_6_MONTHS,
    label: '6ヶ月以内',
    color: 'red-light',
  },
  {
    value: intentionOfJobChangeType.IN_1_YEAR,
    label: '1年以内',
    color: 'red-lightest',
  },
  {
    value: intentionOfJobChangeType.NOT_NOW,
    label: '今は考えていない',
    color: 'grey',
  },
]
export const intentionOfSideJobOptions: IntentionOption[] = [
  {
    value: 1,
    label: '週３-５日前後',
    color: 'primary',
  },
  { value: 2, label: '月30時間前後', color: 'red-light' },
  { value: 3, label: 'スキマ時間で', color: 'red-lightest' },
  { value: 4, label: '今は考えていない', color: 'grey' },
]

export const intentionFilterOptions: { value: number; label: string }[] = [
  { value: 1, label: '転職　3ヶ月以内' },
  { value: 2, label: '転職　6ヶ月以内' },
  { value: 3, label: '転職　1年以内' },
  { value: 4, label: '転職　今は考えていない' },
  { value: 11, label: '副業　週３-５日前後' },
  { value: 12, label: '副業　月30時間前後' },
  { value: 13, label: '副業　スキマ時間で' },
  { value: 14, label: '副業　今は考えていない' },
]

export const lastAccessTypeOptions: { value: number; label: string }[] = [
  { value: 0, label: '指定なし' },
  { value: 1, label: '1週間以内' },
  { value: 2, label: '1ヶ月以内' },
  { value: 3, label: '3ヶ月以内' },
  { value: 4, label: '6ヶ月以内' },
]

// ポートフォリオステータス 0:指定なし 1:公開がある 2:公開・承認制どちらもある
export const portfolioStatusOptions: { value: number; label: string }[] = [
  { value: 0, label: '指定なし' },
  { value: 1, label: '公開がある' },
  { value: 2, label: '公開・承認制どちらかがある' },
]

export const CareerType = {
  COMPANY: 1,
  FREELANCE: 2,
  SCHOOL: 3,
}

export const UserSkillCategoryType = {
  WILL_CAN: 1,
  OPPOSITE: 2,
}

export const UserDesiredConditionOccupationOption = {
  DESIRED: 1,
  NOT_DESIRED: 2,
  NOT_ANSWER: 0,
}

export const PortfolioReleaseStatus = {
  ACCEPTED: 1,
  DENIED: 2,
}

export const USER_SEARCH_DISPLAY_CONDITION_KEYS = [
  'onlyHasDraft',
  'hasNoDraft',
  'onlyHasComment',
  'hasNoComment',
  'excludeOwnRead',
  'excludeMemberRead',
  'excludeTalentPoolRegistered',
  'showAll',
] as const satisfies readonly (keyof UserDisplayCondition)[]

export type UserSearchDisplayConditionKey =
  (typeof USER_SEARCH_DISPLAY_CONDITION_KEYS)[number]

/** スカウト検索で選択可能な表示条件（なしのみ系は企業出身者検索専用のため除外） */
export const SCOUT_SEARCH_DISPLAY_CONDITION_KEYS = [
  'onlyHasDraft',
  'onlyHasComment',
  'excludeOwnRead',
  'excludeMemberRead',
  'excludeTalentPoolRegistered',
  'showAll',
] as const satisfies readonly UserSearchDisplayConditionKey[]

/** APIが同時指定を許さない相反する表示条件のペア（後勝ちで排他制御する） */
export const USER_SEARCH_DISPLAY_CONDITION_CONFLICT_PAIRS: [
  UserSearchDisplayConditionKey,
  UserSearchDisplayConditionKey,
][] = [
  ['onlyHasDraft', 'hasNoDraft'],
  ['onlyHasComment', 'hasNoComment'],
]

export const userSearchDisplayConditionOptions: {
  label: string
  value: UserSearchDisplayConditionKey
  description?: string
}[] = [
  { label: 'スカウト下書きありのみ', value: 'onlyHasDraft' },
  { label: 'スカウト下書きなしのみ', value: 'hasNoDraft' },
  { label: 'コメントありのみ', value: 'onlyHasComment' },
  { label: 'コメントなしのみ', value: 'hasNoComment' },
  { label: '自分の既読を除外', value: 'excludeOwnRead' },
  { label: '自社メンバーの既読を除外', value: 'excludeMemberRead' },
  {
    label: 'タレントプール登録済みを除外',
    value: 'excludeTalentPoolRegistered',
  },
  {
    label: 'スカウト送信不可を含める',
    value: 'showAll',
    description: 'スカウト送信後90日未満の候補者が表示されるようになります。',
  },
]

export const UserSortType = {
  ACCESS_DATE: '0',
  INTENTION_UPDATED_DATE: '1',
  REGISTRATION_DATE: '2',
  PROFILE_UPDATED_DATE: '3',
} as const

export type UserSortValue = (typeof UserSortType)[keyof typeof UserSortType]

export const userSortOptions = [
  { value: UserSortType.ACCESS_DATE, label: 'アクセス順' },
  { value: UserSortType.INTENTION_UPDATED_DATE, label: '意欲変更順' },
  { value: UserSortType.PROFILE_UPDATED_DATE, label: 'プロフィール変更順' },
  { value: UserSortType.REGISTRATION_DATE, label: '登録順' },
]
